import { useEffect, useRef, useState } from 'react';
import { productsAround } from '../../../api/endpoints';

interface Suggestion {
  id: string;
  name1: string;
  name2: string;
  category: string;
}

interface Props {
  /** Enter / 候補クリックで確定したコード（空文字は呼ばない） */
  onEnter: (code: string) => void;
  /** フォーカスアウト時。入力があれば確定する（呼び出し側で入力欄を閉じる） */
  onBlurCommit: (code: string) => void;
  /** ↑↓での隣接明細行への移動 */
  onArrow: (dir: 'up' | 'down') => void;
}

/** 入力中に叩いた近傍候補のキャッシュ（コード → 候補）。同じコードの打ち直しでAPIを再実行しない。 */
const cache = new Map<string, Suggestion[]>();
const CACHE_MAX = 200;
/** 候補を取りに行く最小文字数。1〜2文字では候補が実用にならずAPI負荷だけ増えるため。 */
const MIN_LEN = 3;
const DEBOUNCE_MS = 250;

/** 商品コード直打ち用の入力欄。入力中にコード順の前後5件を候補表示し、
 * Enter を押さなくても商品名を確認できるようにする。
 * ↑↓は従来どおり行移動（候補選択には使わない）、Esc で候補を閉じる。 */
export function CodeSuggestInput({ onEnter, onBlurCommit, onArrow }: Props) {
  const [value, setValue] = useState('');
  const [items, setItems] = useState<Suggestion[]>([]);
  const [pos, setPos] = useState<{ left: number; top: number; width: number } | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  // 最新リクエストの通し番号。古いレスポンスで候補を上書きしない。
  const seqRef = useRef(0);

  const code = value.trim();

  useEffect(() => {
    if (code.length < MIN_LEN) {
      setItems([]);
      return;
    }
    const cached = cache.get(code);
    if (cached) {
      setItems(cached);
      return;
    }
    const timer = setTimeout(() => {
      const seq = ++seqRef.current;
      void (async () => {
        try {
          const data = await productsAround(code, 5);
          if (seq !== seqRef.current) return;
          if (cache.size >= CACHE_MAX) cache.clear();
          cache.set(code, data.items);
          setItems(data.items);
        } catch {
          if (seq === seqRef.current) setItems([]);
        }
      })();
    }, DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [code]);

  // 候補リストは表(overflow)の外へ出すため fixed 配置。スクロール/リサイズで追従する。
  useEffect(() => {
    if (items.length === 0) {
      setPos(null);
      return;
    }
    const update = () => {
      const el = inputRef.current;
      if (!el) return;
      const r = el.getBoundingClientRect();
      setPos({ left: r.left, top: r.bottom + 2, width: Math.max(r.width, 360) });
    };
    update();
    window.addEventListener('scroll', update, true);
    window.addEventListener('resize', update);
    return () => {
      window.removeEventListener('scroll', update, true);
      window.removeEventListener('resize', update);
    };
  }, [items]);

  return (
    <>
      <input
        ref={inputRef}
        autoFocus
        type="text"
        inputMode="numeric"
        placeholder="商品コードを入力"
        title="商品コードを入力してEnter、またはフォーカスを外すと照合します（入力中は前後の候補を表示）"
        className="w-full text-xs border border-primary rounded px-1 py-0.5"
        value={value}
        onChange={(e) => setValue(e.target.value)}
        onKeyDown={(e) => {
          // ↑↓は隣の明細行の入力欄へフォーカス移動（並び替えはしない）。端なら何もしない。
          if (e.key === 'ArrowUp' || e.key === 'ArrowDown') {
            e.preventDefault();
            onArrow(e.key === 'ArrowUp' ? 'up' : 'down');
            return;
          }
          if (e.key === 'Escape') {
            setItems([]);
            return;
          }
          if (e.key !== 'Enter') return;
          if (code) onEnter(code);
        }}
        onBlur={() => onBlurCommit(code)}
      />
      {pos && items.length > 0 && (
        <div
          className="fixed z-50 bg-white border border-border rounded shadow-lg max-h-60 overflow-auto text-xs"
          style={{ left: pos.left, top: pos.top, width: pos.width }}
          // クリック時に input の blur（＝入力中の文字列で確定）が先に走らないようにする。
          onMouseDown={(e) => e.preventDefault()}
        >
          {items.map((s) => (
            <button
              type="button"
              key={s.id}
              onClick={() => onEnter(s.id)}
              title={`${s.name1} ${s.name2} / ${s.category}`}
              className={`w-full text-left px-2 py-1 flex gap-2 items-baseline hover:bg-blue-50 ${
                s.id === code ? 'bg-amber-50' : ''
              }`}
            >
              <span className={`font-mono shrink-0 ${s.id === code ? 'font-bold' : ''}`}>{s.id}</span>
              <span className="flex-1 truncate">{s.name1}</span>
              <span className="text-muted truncate max-w-[8rem]">{s.name2}</span>
            </button>
          ))}
        </div>
      )}
    </>
  );
}
